// ==========================================================
// ฟังก์ชันตัวช่วย (Utilities)
// ==========================================================

export const getStatusConfig = (lotAmount, expDate) => {
  if (lotAmount <= 0) return { bg: '#795548', text: '#fff', label: 'หมด' };
  if (!expDate || expDate === '9999-12-31') return { bg: '#4caf50', text: '#fff', label: 'ปกติ' };
  const today = new Date();
  const expire = new Date(expDate);
  const diffMonths = (expire - today) / (1000 * 60 * 60 * 24 * 30);
  if (expire <= today) return { bg: '#000', text: '#fff', label: 'หมดอายุ' };
  if (diffMonths < 3) return { bg: '#ff4d4d', text: '#fff', label: '< 3ด.' };
  if (diffMonths <= 12) return { bg: '#ff9800', text: '#fff', label: '3ด.-1ปี' };
  return { bg: '#4caf50', text: '#fff', label: 'ปกติ' };
};

export const getSafeTime = (ts) => ts ? new Date(ts).getTime() : 0;

export const isSameDate = (timestamp, datePickerValue) => {
  if (!timestamp || !datePickerValue) return false;
  const d = new Date(timestamp);
  const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return localDate === datePickerValue;
};

export const chunkArray = (arr, size) => Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));

export const generateUID = (loopIndex = 0) => {
  const d = new Date();
  d.setTime(d.getTime() + (loopIndex * 1000)); 
  const base13 = `${d.getFullYear().toString().slice(-2)}${(d.getMonth()+1).toString().padStart(2,'0')}${d.getDate().toString().padStart(2,'0')}${d.getHours().toString().padStart(2,'0')}${d.getMinutes().toString().padStart(2,'0')}${d.getSeconds().toString().padStart(2,'0')}${Math.floor(d.getMilliseconds()/100)}`;
  let sum = 0; for(let i=0; i<13; i++) sum += parseInt(base13[i]) * (i%2===0?1:3);
  return `${base13}${(10-(sum%10))%10}`;
};

export const validateUID = (uid) => {
    if (!uid) return false;
    if (uid.length !== 13 && uid.length !== 14) return false;
    
    const baseLength = uid.length - 1;
    let sum = 0; 
    for(let i=0; i<baseLength; i++) sum += parseInt(uid[i]) * (i%2===0?1:3);
    const checkDigit = (10-(sum%10))%10;
    return parseInt(uid[baseLength]) === checkDigit;
};

export const generateConsumableUID = (loopIndex = 0) => {
  const d = new Date();
  d.setTime(d.getTime() + (loopIndex * 1000));
  const yy = d.getFullYear().toString().slice(-2);
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  const dd = d.getDate().toString().padStart(2, '0');
  const hh = d.getHours().toString().padStart(2, '0');
  const min = d.getMinutes().toString().padStart(2, '0');
  const ss = d.getSeconds().toString().padStart(2, '0');
  const m = Math.floor(d.getMilliseconds() / 100);
  return `CON-${yy}${mm}${dd}-${hh}${min}${ss}${m}`;
};

export const validateConsumableUID = (uid) => {
  return Boolean(uid && typeof uid === 'string' && uid.startsWith('CON-'));
};

// Client-side image compression helper using Canvas
export const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.75) => {
  return new Promise((resolve) => {
    if (
      typeof window === 'undefined' ||
      !window.FileReader ||
      !window.HTMLCanvasElement ||
      !window.Image
    ) {
      return resolve(file);
    }
    
    if (!file || !file.type || !file.type.startsWith('image/')) {
      return resolve(file);
    }

    const reader = new window.FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new window.Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = window.document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return resolve(file);
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) {
            try {
              const compressedFile = new window.File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } catch (e) {
              resolve(blob);
            }
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
};

// Central Cloudinary upload helper with automatic compression
export const uploadImageToCloudinary = async (file, options = {}) => {
  const { maxWidth = 1200, maxHeight = 1200, quality = 0.75 } = options;
  const compressedFile = await compressImage(file, maxWidth, maxHeight, quality);
  const formData = new FormData();
  formData.append('file', compressedFile);

  let metaEnv = {};
  try {
    // eslint-disable-next-line no-new-func
    metaEnv = Function('return import.meta.env')();
  } catch (e) {
    // Ignored
  }

  const uploadPreset = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET) ||
    (typeof process !== 'undefined' && process.env && process.env.VITE_CLOUDINARY_UPLOAD_PRESET) ||
    metaEnv.VITE_CLOUDINARY_UPLOAD_PRESET ||
    'cssd_items';

  const cloudName = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_CLOUDINARY_CLOUD_NAME) ||
    (typeof process !== 'undefined' && process.env && process.env.VITE_CLOUDINARY_CLOUD_NAME) ||
    metaEnv.VITE_CLOUDINARY_CLOUD_NAME ||
    'dwb0oda9m';

  formData.append('upload_preset', uploadPreset);

  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw new Error(`Cloudinary upload failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!data.secure_url) {
    throw new Error('Cloudinary response did not contain secure_url');
  }

  return data.secure_url;
};

